import { Offcanvas, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import { AddBannerInterface } from "./Banner";
import HelperService from "../../utility/HelperService";
import { EyeFill, EyeSlashFill } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { Upload } from "lucide-react";
import CommonQuill from "../../components/editor/CommonQuill";

interface PropData {
  show: boolean;
  handleClose: () => void;
  data: AddBannerInterface | null;
}
const AddBanner = (props: PropData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger,
  } = useForm<AddBannerInterface & { role?: string }>();
  const [isLoading, setLoading] = useState(false);
  const [bladeTitle, setBladeTitle] = useState("");
  const [selectedUser, setSelectedUser] = useState<AddBannerInterface | null>(
    null,
  );
  const [value, setEditorValue] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const quillRef = useRef<any>(null);
  const [customErrors, setCustomErrors] = useState<{
    description?: string;
  }>({});
  const [bannerType, setBannerType] = useState<"IMAGE" | "VIDEO">("IMAGE");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const [videoMimeType, setVideoMimeType] = useState<string>("");
  useEffect(() => {
    if (props.data) {
      setBladeTitle("Edit Banner");
      setBannerType(props.data.type || "IMAGE");
      setVideoPreviewUrl(props.data.video || null);   
      // 👇 detect GIF from existing URL
      if (props.data.video?.toLowerCase().endsWith(".gif")) {
        setVideoMimeType("image/gif");
      } else {
        setVideoMimeType("");
      }         
      reset(props.data);
      setEditorValue(data?.description || "");
      setPreviewUrl(props.data.image || null);
    } else {
      reset({});
      setBannerType("IMAGE");
      setVideoFile(null);
      setVideoPreviewUrl(null);
      setVideoMimeType(""); 
      setBladeTitle("Add Banner");
      setEditorValue("");
      setPreviewUrl("");
      setLogoFile(null);
    }
  }, [props.show]);
  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["video/mp4", "video/webm", "video/ogg", "video/quicktime", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Only MP4, WEBM, OGG, MOV or GIF files are allowed.");
      return;
    }
    setVideoFile(file);
    setVideoMimeType(file.type);
    setVideoPreviewUrl(URL.createObjectURL(file));
  };  
  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    // remove html tags, &nbsp; and spaces
    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };
  const validateInfoTab = async () => {
    const titleValid = await trigger(["title"]);

    const newErrors: any = {};

    if (isQuillEmpty(value)) {
      newErrors.description = "Description is required";
    }

    setCustomErrors(newErrors);

    // 🔥 IMPORTANT: return actual validity
    return titleValid && !newErrors.description;
  };
  const ALLOWED_IMAGE_TYPES = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/svg+xml",
  ];
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];

    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      const msg =
        "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

      toast.error(msg);
      setFile(null);
      setPreviewUrl("");
      input.value = "";
      return;
    }
    setLogoFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };
  const data = props.data;
  const onSubmit = async () => {
    const valid = await validateInfoTab();
    if (!valid) {
      toast.error("Please fill required fields");
      return;
    }

    // 👇 ADD image/video validation
    if (bannerType === "IMAGE" && !logoFile && !previewUrl) {
      toast.error("Please upload an image");
      return;
    }
    if (bannerType === "VIDEO" && !videoFile && !videoPreviewUrl) {
      toast.error("Please upload a video");
      return;
    }    

    setLoading(true);

    const formValues = watch();
    const id = props.data?._id;

    // Build formData
    const formData = new FormData();
    formData.append("title", formValues.title);

    const editor = quillRef.current?.getEditor();
    const html = editor?.root.innerHTML || "";
    formData.append("description", html);
    formData.append("url", formValues.url);
    formData.append("type", bannerType);

    if (bannerType === "IMAGE") {
      if (logoFile) formData.append("image", logoFile);
    } else {
      if (videoFile) formData.append("video", videoFile);
    }

    // 🔥 Select correct API based on add or edit
    const apiCall = id
      ? WebService.fileUploadPutAPI({
          action: `banner/update/${id}`,
          body: formData,
        })
      : WebService.fileUploadAPI({
          action: `banner/add`,
          body: formData,
        });

    apiCall
      .then((res: any) => {
        toast.success(
          id ? "Banner Updated Successfully." : "Banner Created Successfully.",
        );
        setLoading(false);
        handleClose();
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message ||
            "Something went wrong. Please try again later.",
        );
        setLoading(false);
      });
  };

  const handleClose = props.handleClose;
  const show = props.show;

  const onCloseModal = (e?: any) => {
    reset({});
    props.handleClose();
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          reset();
          onCloseModal();
        }}
        placement="end"
        className="offcanvas-custom"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{bladeTitle}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Name */}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title"
                {...register("title", { required: "Please enter title" })}
              />
              {errors.title && (
                <div className="error-message">{errors.title.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Description <span className="text-danger">*</span>
              </Form.Label>

              <CommonQuill
                ref={quillRef}
                value={value}
                onChange={setEditorValue}
                storage="BANNER"
                initialHtml={show && data?.description ? HelperService.normalizeResizeHtml(data.description) : undefined}
                onClearError={() =>
                  setCustomErrors((p) => ({
                    ...p,
                    description: undefined,
                  }))
                }
              />
              {customErrors.description && (
                <div className="error-message">{customErrors.description}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventUrl">
              <Form.Label className="label">Banner Url</Form.Label>

              <Form.Control
                type="text"
                placeholder="Enter url"
                {...register("url", {
                  pattern: {
                    value:
                      /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/,
                    message: "Please enter a valid URL (http/https)",
                  },
                })}
              />

              {errors.url && (
                <div className="error-message">{errors.url.message}</div>
              )}
            </Form.Group>

            {/* Banner Type */}
            <Form.Group className="mb-3">
              <Form.Label className="label">Banner Type</Form.Label>
              <div className="d-flex gap-4">
                <Form.Check
                  type="radio"
                  label="Image"
                  value="IMAGE"
                  checked={bannerType === "IMAGE"}
                  onChange={() => {
                    setBannerType("IMAGE");
                    // setVideoFile(null);
                    // setVideoPreviewUrl(null);
                  }}
                />
                <Form.Check
                  type="radio"
                  label="Video"
                  value="VIDEO"
                  checked={bannerType === "VIDEO"}
                  onChange={() => {
                    setBannerType("VIDEO");
                    // setLogoFile(null);
                    // setPreviewUrl(null);
                  }}
                />
              </div>
            </Form.Group>

            <Form.Label className="label">
              Upload {bannerType === "IMAGE" ? "Image" : "Video"} <span className="text-danger">*</span>
            </Form.Label>
            {/* Conditional Upload */}
            {bannerType === "IMAGE" ? (
              // 👇 existing image upload div as it is
              <div
                className="upload-image"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  aspectRatio: "550 / 100", // 👈 FIX
                  border: "1px dashed #d1d5db",
                  borderRadius: "12px",
                  // overflow: "hidden",
                  backgroundColor: "#f9fafb",
                }}>
                {previewUrl ? (
                  <div style={{ position: "relative", width: "100%", height: "100%" }}>
                    <img
                      src={previewUrl}
                      alt="Image Preview"
                      style={{ width: "100%", height: "100%", objectFit: "contain", backgroundColor: "#fff" }}
                    />
                    {/* 👇 Cross button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setPreviewUrl(null);
                        setLogoFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        background: "#dc3545",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: "22px",
                        height: "22px",
                        fontSize: "14px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1,
                        zIndex: 10,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload size={20} className="text-primary mb-2" />
                    <span className="text-primary text-sm">Click to Upload image</span>
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>
            ) : (
              <div
                className="upload-image"
                onClick={() => videoInputRef.current?.click()}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  aspectRatio: "550 / 100", // 👈 FIX
                  border: "1px dashed #d1d5db",
                  borderRadius: "12px",
                  // overflow: "hidden",
                  backgroundColor: "#fbf9f9",
                }}>
                {videoPreviewUrl ? (
                  <div style={{ position: "relative", width: "100%", height: "100%" }}>
                    {videoMimeType === "image/gif" ? (
                      <img
                        src={videoPreviewUrl}
                        alt="GIF Preview"
                        style={{ width: "100%", maxHeight: "170px", borderRadius: "8px", objectFit: "contain" }}
                      />
                    ) : (
                      <video
                        src={videoPreviewUrl}
                        controls
                        style={{ width: "100%", maxHeight: "180px", borderRadius: "8px" }}
                      />
                    )}
                    {/* 👇 Cross button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setVideoPreviewUrl(null);
                        setVideoFile(null);
                        setVideoMimeType("");
                        if (videoInputRef.current) videoInputRef.current.value = "";
                      }}
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        background: "#dc3545",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: "22px",
                        height: "22px",
                        fontSize: "14px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1,
                        zIndex: 10,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload size={20} className="text-primary mb-2" />
                    <span className="text-primary text-sm">Click to Upload Video (MP4, WEBM, OGG, MOV, GIF)</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="video/*,image/gif"
                  ref={videoInputRef}
                  onChange={handleVideoChange}
                  style={{ display: "none" }}
                />
              </div>
            )}
            <span className="text-primary" style={{ fontSize: "12px" }}>
              {bannerType === "IMAGE"
                ? "Note: Please upload an image of size 550×490 in .png, .jpeg, .jpg or .svg format"
                : "Note: Please upload a video in .mp4, .webm, .ogg, .mov or .gif format"}
            </span>
            <div className="d-flex justify-content-end p-3">
              <CustomButton
                isLoading={isLoading}
                label="Save"
                className="w-100"
                type="submit"
                id="saveUserButton"
              />
            </div>
          </form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default AddBanner;
